import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Moon, Check, X, MapPin } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";
import { http } from "../lib/api";

const waLink = (phone, text) => `https://wa.me/91${phone}?text=${encodeURIComponent(text)}`;
const usePhone = () => useAuth().config.support_phone || "7719264029";

const WhatsAppBtn = ({ pkg, className = "", testid }) => {
  const phone = usePhone();
  return (
    <a href={waLink(phone, `Hi WanderNest, I'm interested in the "${pkg.name}" package (${pkg.nights}N, ${pkg.country}). Please share details and price.`)}
      target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold px-5 py-2.5 text-sm transition-colors ${className}`}>
      <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
    </a>
  );
};

export const PackageCard = ({ p }) => (
  <div className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-shadow flex flex-col" data-testid={`package-card-${p.slug}`}>
    <Link to={`/holidays/${p.slug}`} className="relative h-48 overflow-hidden block">
      <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      {p.tag && <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-[#EC5B24] text-white rounded-full px-2.5 py-1">{p.tag}</span>}
      <span className="absolute bottom-3 left-3 text-xs font-semibold bg-white/90 rounded-full px-2.5 py-1 flex items-center gap-1"><Moon className="h-3 w-3" /> {p.nights}N / {p.nights + 1}D</span>
    </Link>
    <div className="p-4 flex flex-col flex-1">
      <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.country}</div>
      <Link to={`/holidays/${p.slug}`} className="font-heading text-lg font-bold text-slate-900 hover:text-[#EC5B24]">{p.name}</Link>
      <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.highlights.join(" · ")}</div>
      <div className="mt-auto pt-4"><WhatsAppBtn pkg={p} className="w-full" testid={`wa-enquire-${p.slug}`} /></div>
    </div>
  </div>
);

export default function Holidays() {
  const [list, setList] = useState(null);
  const [region, setRegion] = useState("international");
  useEffect(() => { http.get("/packages").then((r) => setList(r.data)); }, []);
  const shown = (list || []).filter((p) => p.region === region);
  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <section className="relative bg-[#1C1A4A] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1800&q=70" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-14">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">Holiday packages</h1>
          <p className="text-slate-300 mt-3 text-base md:text-lg max-w-2xl">International tours and all 28 states of India — hotels, sightseeing and transfers planned for you. Message us on WhatsApp for the best price.</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="flex gap-2 mb-6">
          {[["international", "International"], ["india", "India · 28 states"]].map(([id, l]) => (
            <button key={id} data-testid={`holidays-tab-${id}`} onClick={() => setRegion(id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${region === id ? "bg-[#EC5B24] text-white" : "bg-white text-slate-600 border"}`}>{l}</button>
          ))}
        </div>
        {!list && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="packages-grid">
          {shown.map((p) => <PackageCard key={p.slug} p={p} />)}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export const PackageDetail = () => {
  const { slug } = useParams();
  const phone = usePhone();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [f, setF] = useState({ name: "", date: "", travellers: "2", from: "" });
  useEffect(() => { http.get(`/packages/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <div className="min-h-screen bg-[#F4F6F9]"><Header /><div className="max-w-md mx-auto mt-20 bg-white rounded-2xl p-10 text-center">Package not found. <Link to="/holidays" className="text-[#EC5B24] font-semibold">See all</Link></div></div>;
  if (!p) return <div className="min-h-screen bg-[#F4F6F9]"><Header /><Skeleton className="h-96 max-w-5xl mx-auto mt-8 rounded-2xl" /></div>;
  const msg = `Hi WanderNest, I want the "${p.name}" package (${p.nights}N, ${p.country}).\nName: ${f.name || "-"}\nTravel date: ${f.date || "-"}\nTravellers: ${f.travellers || "-"}\nStarting from: ${f.from || "-"}\nPlease share the price.`;
  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24]";
  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="relative h-72 sm:h-96 bg-[#1C1A4A]">
        <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1A4A] via-transparent" />
        <div className="absolute bottom-0 inset-x-0 mx-auto max-w-6xl px-4 pb-6 text-white">
          <div className="text-sm flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.country} · {p.nights} nights / {p.nights + 1} days</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold mt-1" data-testid="package-title">{p.name}</h1>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-4 py-8 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5 min-w-0">
          <section className="bg-white rounded-2xl border p-6">
            <h2 className="font-heading text-lg font-bold mb-3">Highlights</h2>
            <div className="grid sm:grid-cols-2 gap-2">{p.highlights.map((h) => <div key={h} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-[#0F9D58] shrink-0 mt-0.5" /> {h}</div>)}</div>
          </section>
          <section className="bg-white rounded-2xl border p-6" data-testid="package-itinerary">
            <h2 className="font-heading text-lg font-bold mb-3">Itinerary</h2>
            <ol className="space-y-3">{p.itinerary.map((d, i) => (
              <li key={i} className="flex gap-3 text-sm"><span className="h-7 w-7 rounded-full bg-orange-50 text-[#EC5B24] font-bold grid place-items-center shrink-0 text-xs">{i + 1}</span><span className="pt-1">{d.replace(/^Day \d+: /, "")}</span></li>
            ))}</ol>
          </section>
          <section className="grid sm:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border p-6"><h2 className="font-heading font-bold mb-3">Included</h2>{p.inclusions.map((x) => <div key={x} className="flex gap-2 text-sm mb-1.5"><Check className="h-4 w-4 text-[#0F9D58] shrink-0 mt-0.5" /> {x}</div>)}</div>
            <div className="bg-white rounded-2xl border p-6"><h2 className="font-heading font-bold mb-3">Not included</h2>{p.exclusions.map((x) => <div key={x} className="flex gap-2 text-sm mb-1.5"><X className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /> {x}</div>)}</div>
          </section>
        </div>
        <aside className="bg-white rounded-2xl border p-5 h-fit lg:sticky lg:top-20 space-y-3" data-testid="package-enquiry">
          <div className="font-heading text-lg font-bold">Get the best price</div>
          <p className="text-xs text-slate-500">Prices depend on dates, hotels and group size. Send your details on WhatsApp and we'll reply with a quote.</p>
          <input data-testid="pkg-name" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
          <input data-testid="pkg-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
          <input data-testid="pkg-travellers" inputMode="numeric" placeholder="Number of travellers" value={f.travellers} onChange={(e) => setF({ ...f, travellers: e.target.value.replace(/\D/g, "").slice(0, 3) })} className={inp} />
          <input data-testid="pkg-from" placeholder="Starting city (e.g. Jaigaon, Siliguri)" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className={inp} />
          <a href={waLink(phone, msg)} target="_blank" rel="noopener noreferrer" data-testid="pkg-whatsapp-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold py-3 transition-colors">
            <MessageCircle className="h-5 w-5" /> Send on WhatsApp
          </a>
          <a href={`tel:+91${phone}`} className="block text-center text-sm text-slate-500">or call +91 {phone}</a>
        </aside>
      </div>
      <Footer />
    </div>
  );
};
